import { Link } from "react-router";
import { Icon } from "../../components/ui/Icon";
import { libraryApi } from "../../lib/libraryApi";
import type { Changes, Ver } from "../versions/Versions";
import { compareDocs, parseDoc } from "./parts";
import { labelOf, previousOf, type ShelfItem, type Version } from "./shelf";

// --- The marks: one per version, oldest on the left; the tall one is in use, a hollow one waits for you ---

export function Marks({ it, large = false }: { it: ShelfItem; large?: boolean }) {
  return (
    <span className={`nt${large ? " lg" : ""}`} aria-hidden="true">
      {it.versions.map((v) => (
        <i key={v.id} className={[v.who === "you" ? "y" : "", v.id === it.inUse.id ? "on" : "", it.waits?.id === v.id ? "w" : ""].filter(Boolean).join(" ")} />
      ))}
    </span>
  );
}

export function Tile({ it }: { it: ShelfItem }) {
  return (
    <article className={`lb-t${it.waits ? " waits" : ""}`}>
      <div className="lb-top">
        <span className="ki">
          <Icon name={it.icon} />
        </span>
        <Marks it={it} />
      </div>
      <h3 className="lb-n">
        <Link to={`/library/${encodeURIComponent(it.inUse.id)}`}>{it.title}</Link>
      </h3>
      {it.line && <p className="lb-l">{it.line}</p>}
      <div className="lb-st">
        <span className={it.inUse.who === "you" ? "y" : ""}>{labelOf(it.inUse)}</span>
        {it.waits && <span className="w">v{it.waits.n} waits</span>}
      </div>
    </article>
  );
}

// --- Bodies, fetched once each ---

const bodies = new Map<string, Promise<string>>();
export const bodyOf = (id: string) => {
  let p = bodies.get(id);
  if (!p) {
    p = libraryApi.item(id).then((r) => r.item.body);
    p.catch(() => bodies.delete(id));
    bodies.set(id, p);
  }
  return p;
};

// --- The shared versions list and What changed, for a Library item ---

export const verOf = (v: Version): Ver => ({ id: v.id, n: v.n, label: labelOf(v), mine: v.who === "you", by: v.who === "you" ? "You" : "Flashadz", at: v.at, note: v.note });

/** Two versions' words, part by part. */
export const libLoad = (from: Ver, to: Ver): Promise<Changes> => Promise.all([bodyOf(from.id), bodyOf(to.id)]).then(([a, b]) => compareDocs(parseDoc(a), parseDoc(b)));

/** The version a version came from, for What changed. */
export function previousVer(it: ShelfItem, v: Ver): Ver | null {
  const was = it.versions.find((x) => x.id === v.id);
  const p = was ? previousOf(it, was) : null;
  return p ? verOf(p) : null;
}
